import { lazy, Suspense, useEffect, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';
import { m, useReducedMotion } from 'motion/react';
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Tooltip,
  Truncated,
} from '@customerio/pluma-components/react';
import { useCopilotMeta } from '@customerio/hydra-copilot/react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { buildAiFrom } from '@/features/copilot/utils/build-ai-from';
import { close, Sidebar, SidebarHeader, toggle } from '@/features/sidebar';
import { useSupportChat } from '@/features/support';
import { useAnalytics } from '@/services/analytics';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import { AiEnabledGuard, AiNotEnabledFallback } from '@/components/guards';
import { AccountSetting, useAccountSetting } from '@/services/auth/account-settings';
import { TypeWriterText } from '@/components/feedback/typewriter-text';
import { ENTRANCE_FADE_UP_ANIMATE, entranceFadeUpInitial, entranceStaggerTransition } from '@/utils/motion';
import { AgentBetaLabel } from './components/agent-beta-label';
import { useCopilotSessionContext } from './components/session-provider';
import { useCopilotSidebar } from './hooks/use-copilot-sidebar';
import { useIsAgentFullscreen } from './hooks';

// The message list and the rich-text input bring the markdown renderer and the editor,
// so they load after the frame instead of with the app shell. Both come from one chunk.
const loadChat = () => import('./chat');
const CopilotMessages = lazy(() => loadChat().then((module) => ({ default: module.CopilotMessages })));
const CopilotInput = lazy(() => loadChat().then((module) => ({ default: module.CopilotInput })));

/**
 * Fetch the chat chunk once the browser is idle. The sidebar renders nothing while it is
 * closed, so without this the chunk would only start loading on the first open, and the
 * open's focus on the input would run before the input exists.
 */
function preloadChatWhenIdle() {
  const load = () => {
    // A failed fetch here is retried, and reported, when the panel first renders it.
    loadChat().catch(() => {});
  };
  if (typeof globalThis.requestIdleCallback === 'function') {
    const handle = globalThis.requestIdleCallback(load, { timeout: 5000 });
    return () => globalThis.cancelIdleCallback(handle);
  }
  const handle = globalThis.setTimeout(load, 0);
  return () => globalThis.clearTimeout(handle);
}

/** Sidebar column slot that must flex-grow; plain m.div does not receive Pluma flex props. */
const MotionSidebarMessagesColumn = m.create(Box);

function CopilotActions() {
  const router = useRouter();
  const session = useCopilotSessionContext();
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();

  if (workspace == null) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTriggerButton isIconOnly icon="more" variant="tertiary" size="sm" />
      <DropdownMenuContent>
        <DropdownMenuItem
          icon="add"
          onClick={() => {
            session.reset();
            track('aiAssistant', 'newChatClicked', {});
          }}
        >
          New chat
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="chat"
          onClick={() => {
            toggle('copilot-history');
          }}
        >
          All chats
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="sparkle"
          href={
            router.buildLocation({
              to: '/workspaces/$workspaceId/settings/ai-business-profile',
              params: { workspaceId: workspace.id },
            }).href
          }
        >
          Context
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="trigger-date"
          href={
            router.buildLocation({
              to: '/workspaces/$workspaceId/agent/routines',
              params: { workspaceId: workspace.id },
            }).href
          }
        >
          Routines
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="star"
          href={
            router.buildLocation({ to: '/workspaces/$workspaceId/agent/skills', params: { workspaceId: workspace.id } })
              .href
          }
        >
          Skills
        </DropdownMenuItem>
        <DropdownMenuItem icon="manage" href="/settings/ai">
          AI settings
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CopilotName() {
  const session = useCopilotSessionContext();
  const meta = useCopilotMeta(session);
  return (
    <Truncated>
      <TypeWriterText text={meta.name ?? 'The Agent'} />
    </Truncated>
  );
}

export function CopilotSupportButton() {
  const supportChat = useSupportChat();

  if (!supportChat.isVisible || !supportChat.isHandoverAllowed || !supportChat.zendeskChatEnabled) {
    return null;
  }

  return (
    <Tooltip content="Chat with support" isCustom={true}>
      {(context) => (
        <Button
          variant="tertiary"
          onClick={async () => {
            const success = await supportChat.openChat();
            if (success) {
              close();
            }
          }}
          isLoading={supportChat.isLoading}
          size="sm"
          isIconOnly={true}
          icon="chat-question"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        />
      )}
    </Tooltip>
  );
}

function AiAgentTitle() {
  return <>Agent</>;
}

export function SidebarChat() {
  const router = useRouter();
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();
  const { handleToggle } = useCopilotSidebar();
  const safeNavigate = useSafeNavigate();
  const isAgentFullscreen = useIsAgentFullscreen();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const isAiEnabled = useAccountSetting(AccountSetting.AiEnabled);
  // Keyed on the setting rather than run on mount: AI can be turned on mid-session, since the
  // setting updates the account store in place.
  useEffect(() => (isAiEnabled ? preloadChatWhenIdle() : undefined), [isAiEnabled]);
  if (workspace == null) {
    return null;
  }

  const sidebarPanelInitial = entranceFadeUpInitial(shouldReduceMotion);

  return (
    <Sidebar
      ref={sidebarRef}
      id="copilot"
      onToggle={handleToggle}
      onToggleComplete={(isOpen) => {
        if (isOpen) {
          sidebarRef.current?.querySelector<HTMLElement>('#copilot-chat-input')?.focus();
        }
      }}
    >
      <Box display="flex" flexDirection="column" h="full" w="full">
        <m.div
          initial={sidebarPanelInitial}
          animate={ENTRANCE_FADE_UP_ANIMATE}
          transition={entranceStaggerTransition(0, shouldReduceMotion)}
        >
          <SidebarHeader
            title={
              <AiEnabledGuard fallback={AiAgentTitle}>
                <CopilotName />
              </AiEnabledGuard>
            }
            titleSuffix={
              <AiEnabledGuard>
                <AgentBetaLabel />
              </AiEnabledGuard>
            }
            leadingActions={
              <AiEnabledGuard>
                <CopilotActions />
              </AiEnabledGuard>
            }
            trailingActions={
              <AiEnabledGuard>
                <CopilotSupportButton />

                {!isAgentFullscreen ? (
                  <Tooltip content="Open full screen" isCustom={true}>
                    {(context) => (
                      <Button
                        variant="tertiary"
                        size="sm"
                        icon="expand"
                        isIconOnly={true}
                        onClick={() => {
                          if (workspace != null) {
                            track('aiAssistant', 'expandedToFullScreen', {});
                            safeNavigate(
                              router.buildLocation({
                                to: '/workspaces/$workspaceId/agent',
                                params: { workspaceId: workspace.id },
                                search: { 'ai-from': buildAiFrom() },
                              }).href,
                            );
                          }
                        }}
                        ref={context.reference.setReference}
                        {...context.reference.getReferenceProps()}
                      />
                    )}
                  </Tooltip>
                ) : null}
              </AiEnabledGuard>
            }
            onClose={() => {
              toggle('copilot');
            }}
            // Closing parks the conversation as a footer bar chip rather than ending it.
            closeControl={{ icon: 'subtract', label: 'Minimize sidebar' }}
          />
        </m.div>
        <AiEnabledGuard fallback={AiNotEnabledFallback}>
          {/* The sidebar sits outside the layout's error boundary, so a failed chunk load would
              replace the whole shell. Losing the panel body is the better failure; a retry would
              not help, because React.lazy keeps the rejected import. */}
          <AppErrorBoundary fallback={null}>
            <Suspense fallback={null}>
              <MotionSidebarMessagesColumn
                display="flex"
                flexDirection="column"
                flexGrow="1"
                minH="0"
                w="full"
                initial={sidebarPanelInitial}
                animate={ENTRANCE_FADE_UP_ANIMATE}
                transition={entranceStaggerTransition(1, shouldReduceMotion)}
              >
                <CopilotMessages />
              </MotionSidebarMessagesColumn>
              <m.div
                initial={sidebarPanelInitial}
                animate={ENTRANCE_FADE_UP_ANIMATE}
                transition={entranceStaggerTransition(2, shouldReduceMotion)}
              >
                <Box p="100">
                  <CopilotInput />
                </Box>
              </m.div>
            </Suspense>
          </AppErrorBoundary>
        </AiEnabledGuard>
      </Box>
    </Sidebar>
  );
}
